import { useCallback, useEffect, useState } from 'react';

import { FormProvider, useForm } from 'react-hook-form';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import {
  MembershipRead,
  MembershipWrite,
  PatchedMembershipRead,
  useLazyMembersListQuery,
  useMembersCreateMutation,
  useMembersDestroyMutation,
  useMembersPartialUpdateMutation,
  useMembersUndeleteCreateMutation,
} from 'src/common/external/bambi-api/bambiApi';
import { useModal } from 'src/common/modals/useModal';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';

import { setSelectedMember, setShowMemberForm } from '../member.slice';
import { setShowMemberProfilePromptModal } from '../team.slice';
import { AddEditModal } from './AddEditModal';
import { assignDefaultFormValues } from './assignDefaultFormValues';
import { FormValues } from './FormValues';
import { generateMemberAddSuccessToast } from './generateMemberAddSuccessToast';
import { generateMemberDeleteSuccessToast } from './generateMemberDeleteSuccessToast';
import { generateMemberEditSuccessToast } from './generateMemberEditSuccessToast';
import { MemberProfilePromptModal } from './MemberProfilePromptModal';
import { MemberRestorePromptModal } from './MemberRestorePromptModal';

export function MemberForm() {
  const member = useAppSelector((state) => state.member.selectedMember);

  const defaultValues = assignDefaultFormValues(member);
  const methods = useForm<FormValues>({
    defaultValues,
  });

  const open = useAppSelector((state) => state.member.showMemberForm);
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [serverErrors, setServerErrors] = useState<string | null>('');

  const duplicateModalProps = useModal();
  const [duplicateMembership, setDuplicateMembership] =
    useState<MembershipRead | null>(null);

  const [createMember] = useMembersCreateMutation();
  const [deleteMember] = useMembersDestroyMutation();
  const [updateMember] = useMembersPartialUpdateMutation();
  const [restoreMember] = useMembersUndeleteCreateMutation();

  const [findMemberQuery] = useLazyMembersListQuery({});

  const onSubmit = async (data: FormValues) => {
    setLoading(true);

    try {
      // Reset
      setDuplicateMembership(null);
      duplicateModalProps.setOpen(false);

      const response = await findMemberQuery({
        deletedObjects: 'include',
        search: data.email,
      });

      if (response.data?.results.length) {
        const duplicateMember = response.data.results.find(
          (m) => m.email.toLowerCase() === data.email.toLowerCase()
        );
        const duplicateMemberIsDeleted = !!duplicateMember?.deleted_at;

        if (duplicateMember && duplicateMemberIsDeleted) {
          setDuplicateMembership(duplicateMember);
          // Since we've detected a duplicate member, prevent create/update attempt
          setLoading(false);
          // Show the restore modal
          duplicateModalProps.setOpen(true);
          return;
        }
      }
    } catch {
      // Let any errors fall through to the actual submit. Duplicates will still
      // be caught by the API
    }

    try {
      if (member) {
        // TODO: Format payers select options to whatever the api needs
        const patchedMembership: PatchedMembershipRead = {
          ...data,
          role: data.role?.value,
        };

        await updateMember({
          id: member.id,
          patchedMembership,
        }).unwrap();

        dispatch(show(generateMemberEditSuccessToast(data.first_name)));
      } else {
        // TODO: Format payers select options to whatever the api needs
        const membership: MembershipWrite = {
          ...data,
          role: data.role?.value,
          // Further, active_payers is required on the type, which is true for the GET endpoint but not the POST endpoint
          // So, we just cast here
        } as MembershipWrite;

        const newMember = await createMember({
          membership,
        }).unwrap();

        dispatch(show(generateMemberAddSuccessToast(data.first_name)));

        if (data.role?.value === 'associate') {
          dispatch(
            setShowMemberProfilePromptModal({
              open: true,
              member: newMember,
            })
          );
        }
      }

      dispatch(setSelectedMember(null));
      setServerErrors(null);
      dispatch(setShowMemberForm(false));
      methods.reset();
    } catch (error) {
      const formattedErr = formatServerError(error);
      setServerErrors(formattedErr);
    }
    setLoading(false);
  };

  const handleDeleteMember = async () => {
    if (!member) {
      return;
    }

    try {
      await deleteMember({
        id: member.id,
      }).unwrap();
      dispatch(show(generateMemberDeleteSuccessToast(member.first_name)));
      dispatch(setSelectedMember(null));
      setServerErrors(null);
      dispatch(setShowMemberForm(false));
    } catch (error) {
      const formattedErr = formatServerError(error);
      setServerErrors(formattedErr);
    }
  };

  const handleRestoreMember = useCallback(
    async (targetMember?: MembershipRead) => {
      const memberToUse = targetMember ? targetMember : member;
      if (!memberToUse) {
        return;
      }

      try {
        await restoreMember({
          id: memberToUse.id,
          membership: memberToUse,
        }).unwrap();
        dispatch(
          show({
            type: 'success',
            title: 'Restored membership',
            description: `Restored membership for ${memberToUse.first_name}`,
          })
        );
        dispatch(setSelectedMember(null));
        setServerErrors(null);
        dispatch(setShowMemberForm(false));
        duplicateModalProps.setOpen(false);
      } catch (error) {
        const formattedErr = formatServerError(error);
        setServerErrors(formattedErr);
      }
    },
    [dispatch, member, restoreMember, setServerErrors, duplicateModalProps]
  );

  useEffect(() => {
    if (open) {
      methods.reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <FormProvider {...methods}>
      <AddEditModal
        open={open}
        setOpen={(val) => dispatch(setShowMemberForm(val))}
        onCancel={() => {
          setServerErrors(null);
          dispatch(setShowMemberForm(false));
          methods.reset();
        }}
        onDelete={handleDeleteMember}
        onRestore={handleRestoreMember}
        onClose={() => {
          setServerErrors(null);
          dispatch(setSelectedMember(null));
          methods.reset();
        }}
        onSubmit={methods.handleSubmit(onSubmit)}
        errors={methods.formState.errors}
        isReadyToSubmit={Object.keys(methods.formState.errors).length < 1}
        loading={loading}
        control={methods.control}
        serverErrors={serverErrors}
      />
      <MemberProfilePromptModal />
      <MemberRestorePromptModal
        {...duplicateModalProps}
        membership={duplicateMembership}
        onRestoreClick={handleRestoreMember}
      />
    </FormProvider>
  );
}
